/**
 * The two downloadable files of a version — the Disk 1 and Disk 2 installer
 * images — as the command center's `ReleaseArtifactTiles` draws them.
 *
 * THERE IS NO RELEASE-NOTES TILE (2026-09-16). The CHANGE LOG is the release
 * notes, and it is the only copy: the page used to show the same words three
 * times — this tile, the change log beside it, and a documentation article
 * projected from it. portal-api's `ReleaseArtifact::KINDS` is now `disk1` +
 * `disk2` alone, a listing can no longer contain anything else, and a download
 * naming a retired kind is refused upstream.
 *
 * THESE BUTTONS ARE THE SAME AUDITED REQUEST AS EVERYWHERE ELSE. A tile never
 * carries the file's address; pressing Download POSTs to portal-api, which
 * decides from the caller's standing, RECORDS the attempt whether it grants
 * or refuses, and hands back a five-minute URL. A refusal is shown in the
 * server's own words — it already decided and already logged it.
 *
 * This is the reason the restricted tier exists: a reseller installs XOP for
 * its own customers and needs the media a customer never sees. The page does
 * not decide that; portal-api's `Releases::ArtifactAccess` narrows the list
 * per audience before serialising it.
 *
 * EVERY ARTIFACT IS A LINK. A disk image is multiple gigabytes, so nothing is
 * ever uploaded — which is why there is no "external source" chip any more:
 * the flag it read (`linked`) no longer exists, and marking every tile would
 * say nothing.
 */
import { Alert, Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Download, HardDrive } from "lucide-react";
import { useState, type JSX } from "react";

import { PageLoader } from "../../../components/ui/page-loader.js";
import {
  DownloadRefused,
  type ArtifactKind,
  type ReleaseArtifact,
  type ReleaseRow,
  formatBytes,
  listArtifacts,
  requestDownload,
  startDownload,
} from "./api.js";

/** The two kinds, in the order the tiles are laid out. A CLOSED set: a kind
 *  with nothing attached still draws its tile, saying so. */
export const ARTIFACT_KINDS: readonly ArtifactKind[] = ["disk1", "disk2"];

export const ARTIFACT_LABELS: Record<ArtifactKind, string> = {
  disk1: "Disk 1 Image",
  disk2: "Disk 2 Image",
};

/** Who may take each kind — the audience line under the label. Both surviving
 *  kinds are the restricted tier; the one thing everyone could read was the
 *  release-notes document, and that is the change log now. */
export const ARTIFACT_AUDIENCE: Record<ArtifactKind, string> = {
  disk1: "Resellers, sales and super admins only",
  disk2: "Resellers, sales and super admins only",
};

interface Props {
  release: ReleaseRow;
}

export function ReleaseArtifactTiles({ release }: Props): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [pending, setPending] = useState<ArtifactKind | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["reseller", "release-artifacts", release.id],
    queryFn: () => listArtifacts(release.id),
  });

  const byKind = new Map<string, ReleaseArtifact>();
  for (const artifact of data ?? []) byKind.set(artifact.kind, artifact);

  // A download is a REQUEST, not a link — see the module note.
  async function download(kind: ArtifactKind): Promise<void> {
    setPending(kind);
    try {
      const ticket = await requestDownload(release.id, kind);
      setRefusal(null);
      setNotice(`${ARTIFACT_LABELS[kind]} — download starting. This download has been recorded.`);
      startDownload(ticket.url, ticket.filename);
    } catch (e) {
      setNotice(null);
      setRefusal(
        e instanceof DownloadRefused && e.message
          ? e.message
          : "That download could not be started. Please try again.",
      );
    } finally {
      setPending(null);
    }
  }

  if (isLoading) return <PageLoader />;
  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ fontSize: 13 }}
        action={
          <Button size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {error instanceof DownloadRefused && error.message
          ? error.message
          : "Could not load this version's downloads."}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ fontSize: 13 }}>
          {notice}
        </Alert>
      )}
      {refusal && (
        <Alert severity="warning" onClose={() => setRefusal(null)} sx={{ fontSize: 13 }}>
          {refusal}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        {ARTIFACT_KINDS.map((kind) => {
          const artifact = byKind.get(kind);
          return (
            <Box
              key={kind}
              data-testid={`release-${release.version}-tile-${kind}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 1,
                px: 1.5,
                py: 1.5,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: (t) => alpha(t.palette.warning.main, 0.05),
                opacity: artifact && !artifact.active ? 0.6 : 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Box component="span" sx={{ display: "inline-flex", color: "warning.dark" }}>
                    <HardDrive size={16} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {ARTIFACT_LABELS[kind]}
                  </Typography>
                  {artifact && !artifact.active && <Chip label="Retired" size="small" sx={{ height: 18 }} />}
                </Box>
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{ARTIFACT_AUDIENCE[kind]}</Typography>
                {artifact ? (
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                    {artifact.filename ?? "Attached"} · {formatBytes(artifact.byte_size)}
                    {artifact.checksum && (
                      <Box
                        component="span"
                        sx={{ display: "block", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                      >
                        {artifact.checksum}
                      </Box>
                    )}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.25 }}>
                    Not published yet.
                  </Typography>
                )}
              </Box>
              {artifact && (
                <Tooltip title="Download — every request is recorded">
                  <Box component="span" sx={{ display: "flex", mt: "auto" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<Download size={14} />}
                      disabled={pending !== null}
                      aria-label={`Download ${ARTIFACT_LABELS[kind]} (${release.version})`}
                      onClick={() => void download(kind)}
                    >
                      {pending === kind ? "Starting…" : "Download"}
                    </Button>
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
